import React, { useState, useMemo, useCallback, useEffect, useRef } from 'react';

// =============== Helpers ===============
const fmtMoney = (n, opts = {}) => {
  if (n === 0 || n == null) return opts.dash ? '—' : '$0';
  const neg = n < 0;
  const abs = Math.abs(n);
  const hasDecimals = Math.round(abs) !== abs;
  const formatted = '$' + abs.toLocaleString('en-US', { minimumFractionDigits: hasDecimals ? 2 : 0, maximumFractionDigits: 2 });
  if (opts.parens && neg) return '(' + formatted + ')';
  return (neg ? '-' : '') + formatted;
};
const fmtPct = (n, total) => {
  if (!total || total === 0) return '—';
  const p = (n / total) * 100;
  if (!isFinite(p)) return '—';
  return p.toFixed(2) + '%';
};

const TYPE_LABEL = { system: 'Auto', manual: 'User', calculated: 'Calc', custom: 'Custom' };

// Icons
const Icon = {
  chevron: (p) => (
    <svg width="12" height="12" viewBox="0 0 12 12" fill="none" {...p}>
      <path d="M4.5 3L7.5 6L4.5 9" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  ),
  plus: (p) => (
    <svg width="12" height="12" viewBox="0 0 12 12" fill="none" {...p}>
      <path d="M6 2.5V9.5M2.5 6H9.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    </svg>
  ),
  trash: (p) => (
    <svg width="12" height="12" viewBox="0 0 12 12" fill="none" {...p}>
      <path d="M2.5 3.5H9.5M4.5 3.5V2.5C4.5 2.22 4.72 2 5 2H7C7.28 2 7.5 2.22 7.5 2.5V3.5M3.5 3.5V9.5C3.5 9.78 3.72 10 4 10H8C8.28 10 8.5 9.78 8.5 9.5V3.5" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  ),
  lock: (p) => (
    <svg width="11" height="11" viewBox="0 0 12 12" fill="none" {...p}>
      <rect x="2.5" y="5.5" width="7" height="5" rx="1" stroke="currentColor" strokeWidth="1.2" />
      <path d="M4 5.5V4C4 2.9 4.9 2 6 2C7.1 2 8 2.9 8 4V5.5" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" />
    </svg>
  ),
  info: (p) => (
    <svg width="12" height="12" viewBox="0 0 12 12" fill="none" {...p}>
      <circle cx="6" cy="6" r="4.5" stroke="currentColor" strokeWidth="1.2" />
      <path d="M6 5.5V8.5M6 3.8V4" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" />
    </svg>
  ),
  search: (p) => (
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" {...p}>
      <circle cx="6" cy="6" r="4" stroke="currentColor" strokeWidth="1.4" />
      <path d="M9 9L12 12" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
    </svg>
  ),
  close: (p) => (
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" {...p}>
      <path d="M3 3L11 11M11 3L3 11" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    </svg>
  ),
  download: (p) => (
    <svg width="12" height="12" viewBox="0 0 12 12" fill="none" {...p}>
      <path d="M6 2V8M6 8L4 6M6 8L8 6M2.5 9.5H9.5" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  ),
  filter: (p) => (
    <svg width="12" height="12" viewBox="0 0 12 12" fill="none" {...p}>
      <path d="M2 3H10M4 6H8M5 9H7" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
    </svg>
  ),
  save: (p) => (
    <svg width="12" height="12" viewBox="0 0 12 12" fill="none" {...p}>
      <path d="M3 2.5H8L9.5 4V9.5C9.5 9.77 9.27 10 9 10H3C2.73 10 2.5 9.77 2.5 9.5V3C2.5 2.73 2.73 2.5 3 2.5Z" stroke="currentColor" strokeWidth="1.2" />
      <path d="M4 2.5V4.5H7.5V2.5" stroke="currentColor" strokeWidth="1.2" />
    </svg>
  ),
  sliders: (p) => (
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" {...p}>
      <path d="M2 4H9M11 4H12M2 10H5M7 10H12" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
      <circle cx="10" cy="4" r="1.3" fill="currentColor" />
      <circle cx="6" cy="10" r="1.3" fill="currentColor" />
    </svg>
  ),
};

// =============== Type pill ===============
function TypePill({ type }) {
  return <span className={`type-pill type-${type}`}>{TYPE_LABEL[type] || type}</span>;
}

// =============== Disclosure ===============
function Disclosure({ open, onClick, leaf }) {
  if (leaf) return <span className="disclosure leaf" />;
  
  const handleClick = (e) => {
    e.stopPropagation();
    onClick(e);
  };
  
  return (
    <button className={`disclosure ${open ? 'open' : ''}`} onClick={handleClick} aria-label={open ? 'Collapse' : 'Expand'}>
      <Icon.chevron />
    </button>
  );
}

// =============== Summary bar ===============
function SummaryBar({ totals }) {
  const items = [
    { key: 'income', label: 'Total Revenue' },
    { key: 'cogs', label: 'Cost of Goods' },
    { key: 'gross_profit', label: 'Gross Profit' },
    { key: 'expenses', label: 'Total Expenses' },
    { key: 'net_income', label: 'Net Income', accent: true },
  ];
  return (
    <div className="summary-bar">
      {items.map((it) => {
        const v = totals[it.key] || 0;
        return (
          <div key={it.key} className={`summary-cell ${it.accent ? 'accent' : ''}`}>
            <span className="label">{it.label}</span>
            <span className="value" style={v < 0 ? { color: 'var(--neg)' } : {}}>{fmtMoney(v)}</span>
          </div>
        );
      })}
    </div>
  );
}

export { fmtMoney, fmtPct, Icon, TypePill, Disclosure, SummaryBar };
